import { endOfDay, isAfter, isBefore, startOfDay, startOfTomorrow } from "date-fns";
import { z } from "zod";

export interface BidRequestSchemaMessages {
  required: string;
  quantityPositive: string;
  itemsRequired: string;
  endAfterStart: string;
  endNotBeforeTomorrow: string;
  deliveryAfterPostingEnd: string;
}

export const createBidRequestSchema = (messages: BidRequestSchemaMessages) =>
  z
    .object({
      items: z
        .array(
          z.object({
            commodity: z.string().min(1, { message: messages.required }),
            quantity: z
              .string()
              .min(1, { message: messages.required })
              .refine(value => Number(value) > 0, { message: messages.quantityPositive }),
            unit_of_measure: z.string().min(1, { message: messages.required }),
            quality: z.string().min(1, { message: messages.required }),
          })
        )
        .min(1, { message: messages.itemsRequired }),
      delivery_date: z.date().nullable(),
      delivery_location: z.string(),
      posting_start_date: z.date().nullable(),
      posting_end_date: z.date().nullable(),
    })
    .superRefine((values, ctx) => {
      if (!values.delivery_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["delivery_date"],
          message: messages.required,
        });
      }

      if (!values.posting_start_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["posting_start_date"],
          message: messages.required,
        });
      }

      if (!values.posting_end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["posting_end_date"],
          message: messages.required,
        });
        return;
      }

      if (isBefore(startOfDay(values.posting_end_date), startOfTomorrow())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["posting_end_date"],
          message: messages.endNotBeforeTomorrow,
        });
      }

      if (
        values.posting_start_date &&
        !isBefore(startOfDay(values.posting_start_date), startOfDay(values.posting_end_date))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["posting_end_date"],
          message: messages.endAfterStart,
        });
      }

      if (
        values.delivery_date &&
        !isAfter(values.delivery_date, endOfDay(values.posting_end_date))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["delivery_date"],
          message: messages.deliveryAfterPostingEnd,
        });
      }
    });
